/**
 * The reader's place in the URL (web.md §3 "Routes"): `/f/:folderId[/m/:messageId]`,
 * `/v/:viewId[/m/:messageId]`, `/corrections[/m/:messageId]`. Following a link pushes history, so the
 * browser's (and the phone's) Back returns to the list with its scroll position.
 */

import { useCallback } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import type { FolderId, MessageId, ViewId } from '@digester/core';

export type ReaderScope = { kind: 'folder'; folderId: FolderId } | { kind: 'view'; viewId: ViewId } | { kind: 'corrections' };

export interface ReaderRoute {
  scope: ReaderScope;
  messageId: MessageId | null;
}

export function scopePath(scope: ReaderScope): string {
  if (scope.kind === 'folder') return `/f/${encodeURIComponent(scope.folderId)}`;
  if (scope.kind === 'view') return `/v/${encodeURIComponent(scope.viewId)}`;
  return '/corrections';
}

export function messagePath(scope: ReaderScope, messageId: MessageId): string {
  return `${scopePath(scope)}/m/${encodeURIComponent(messageId)}`;
}

/** Parse a reader pathname (null for anything else). */
export function parseReaderPath(pathname: string): ReaderRoute | null {
  const parts = pathname.split('/').filter(Boolean).map(decodeURIComponent);
  let scope: ReaderScope | null = null;
  let rest: string[] = [];
  if (parts[0] === 'f' && parts[1]) {
    scope = { kind: 'folder', folderId: parts[1] as FolderId };
    rest = parts.slice(2);
  } else if (parts[0] === 'v' && parts[1]) {
    scope = { kind: 'view', viewId: parts[1] as ViewId };
    rest = parts.slice(2);
  } else if (parts[0] === 'corrections') {
    scope = { kind: 'corrections' };
    rest = parts.slice(1);
  }
  if (!scope) return null;
  const messageId = rest[0] === 'm' && rest[1] ? (rest[1] as MessageId) : null;
  return { scope, messageId };
}

/** The current reader route plus navigation helpers. */
export function useReaderRoute(): ReaderRoute & { openMessage(id: MessageId, options?: { replace?: boolean }): void; closeMessage(): void } {
  const location = useLocation();
  useParams();
  const navigate = useNavigate();
  const route = parseReaderPath(location.pathname) ?? { scope: { kind: 'folder', folderId: 'inbox' as FolderId }, messageId: null };
  const openMessage = useCallback(
    (id: MessageId, options?: { replace?: boolean }) => navigate(messagePath(route.scope, id), { replace: options?.replace ?? false }),
    [navigate, route.scope],
  );
  const closeMessage = useCallback(() => navigate(scopePath(route.scope)), [navigate, route.scope]);
  return { ...route, openMessage, closeMessage };
}
